import { Box, Chip, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, SxProps, Tooltip } from "@mui/material";
import React, { useState } from "react";
import { useSharedFoodGroupsQuery } from "../../types";
import { SimpleFoodGroup } from "../Food/Form/FoodDetails/FoodDetailsSection";
import { DEFAULT_LIMIT } from "./utils";

interface FoodGroupMultiSelectProps {
  handleFoodGroupIsChanged: (foodGroups: SimpleFoodGroup[]) => void;
  values: readonly { name: string; id: string }[];
  onChange: (values: SimpleFoodGroup[]) => void;
  error: boolean;
  helperText?: string;
  sx?: SxProps;
}

export const FoodGroupMultiSelect = ({ values, handleFoodGroupIsChanged }: FoodGroupMultiSelectProps) => {
  const { data } = useSharedFoodGroupsQuery({
    variables: { searchTerm: "", limit: DEFAULT_LIMIT },
  });

  const [selectedItems, setSelectedItems] = useState(values);
  const MAX_CHARS = 11;
  const DEFAULT_VISIBLE_CHIPS = 4;
  const reducedMaxVisibleChips = DEFAULT_VISIBLE_CHIPS - 2;

  const hasLongName = values.some(foodGroup => foodGroup.name.length > MAX_CHARS);

  const maxVisibleChips = hasLongName ? reducedMaxVisibleChips : DEFAULT_VISIBLE_CHIPS;

  const renderChips = (selected: string[]) => {
    // Find the full foodGroup objects based on the selected names passed in
    const selectedFoodGroups = selected.map(name => data?.sharedFoodGroups.find(foodGroup => foodGroup.name === name)).filter(Boolean);

    const visibleItems = selectedFoodGroups.slice(0, maxVisibleChips);
    const overflowCount = selectedFoodGroups.length - maxVisibleChips;

    return (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {visibleItems.map(foodGroup => {
          if (!foodGroup) {
            return null;
          }

          return (
            <Chip
              key={foodGroup.id}
              label={foodGroup.name}
              onMouseDown={e => e.stopPropagation()}
              onDelete={handleDelete(foodGroup.id)}
              size="small"
            />
          );
        })}
        {overflowCount > 0 && (
          <Tooltip
            title={selectedFoodGroups
              .slice(maxVisibleChips)
              .map(fg => fg?.name)
              .join(", ")}
          >
            <Chip label={`+${values.length - maxVisibleChips}`} size="small" />
          </Tooltip>
        )}
      </Box>
    );
  };

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    const selectedFoodGroups = (typeof value === "string" ? value.split(",") : value)
      .map(name => data?.sharedFoodGroups.find(group => group.name === name))
      .filter(Boolean) as SimpleFoodGroup[];

    handleFoodGroupIsChanged(selectedFoodGroups);
    setSelectedItems(selectedFoodGroups);
  };

  const handleDelete = (chipIdToDelete: string) => () => {
    const updatedValues = selectedItems.filter(foodGroup => foodGroup.id !== chipIdToDelete);
    handleFoodGroupIsChanged(updatedValues);
    setSelectedItems(updatedValues);
  };

  return (
    <FormControl fullWidth size="small">
      <InputLabel id="food-group-chip-label">Food Groups</InputLabel>
      <Select
        labelId="food-group-chip-label"
        label="Food Groups"
        id="food-group-chip"
        fullWidth
        multiple
        value={selectedItems.map(foodGroup => foodGroup.name)}
        onChange={handleChange}
        renderValue={selected => renderChips(selected)}
        //styling here ensures menu drops below the select field
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 48 * 4.5 + 8,
              width: 250,
            },
          },
        }}
      >
        {data?.sharedFoodGroups.map(foodGroup => (
          <MenuItem
            key={foodGroup.id}
            value={foodGroup.name}
            sx={{ fontWeight: selectedItems.find(fg => fg.id === foodGroup.id) ? "bold" : "normal" }}
          >
            {foodGroup.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
